import React, { type ReactElement, type ReactNode } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@ndd/ui";
import type {
  MyAlertsResponse,
  NotificationDefaultsRdOption,
  NotificationDefaultsResponse,
  NotificationDefaultsSetting,
} from "../../../../shared/api-types";
import {
  THEMED_CARD,
  THEMED_CARD_TITLE,
  THEMED_PAGE_TITLE,
} from "../../lib/always-look";
import {
  MESSAGE_TYPES,
  editorRows,
  followsPractice,
  reminderTimeLabel,
  rowKey,
  shownRow,
  withEnabled,
  withReminderTime,
  FOLLOW_PRACTICE,
  MY_ALERT_SWITCHES,
  minuteToTime,
  timeToMinute,
  withAgendaEnabled,
  withAgendaTime,
  type DefaultsDraft,
  type MyAlertsDraft,
  type DraftRow,
  type EditorLayer,
} from "../../lib/notification-settings-client";

// The Notification settings page (NO-834): white cards on cream, one per
// section, each with its own Save. A leaf module (`import React`, relative
// imports, no router, no queries) so `tsx --test` renders it; the page
// supplies the data, the draft and the save.

export function NotificationSettingsHeading(): ReactElement {
  return (
    <h1
      className={cn(
        "font-sans text-3xl font-bold tracking-tight text-foreground",
        THEMED_PAGE_TITLE
      )}
    >
      Notification settings
    </h1>
  );
}

export function SettingsCard({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  children: ReactNode;
}): ReactElement {
  const headingId = `${id}-heading`;

  return (
    <section
      aria-labelledby={headingId}
      data-settings-section={id}
      className={cn(
        "mt-6 rounded-xl border border-border bg-card p-6 text-card-foreground shadow-sm",
        THEMED_CARD
      )}
    >
      <h2
        id={headingId}
        className={cn(
          "font-sans text-lg font-semibold text-foreground",
          THEMED_CARD_TITLE
        )}
      >
        {title}
      </h2>
      {description ? (
        <p className="mt-1 text-body-sm text-muted-foreground">{description}</p>
      ) : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export type SaveStatus =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; changedByOthers: string[] }
  | { kind: "conflict" }
  | { kind: "failed"; message: string };

function describeDescription(kind: string): string {
  return MESSAGE_TYPES.find((type) => type.kind === kind)?.description ?? "";
}

function labelOf(kind: string): string {
  return MESSAGE_TYPES.find((type) => type.kind === kind)?.label ?? kind;
}

function ReminderTimes({
  id,
  row,
  draft,
  timingMenuMinutes,
  disabled,
  onDraft,
}: {
  id: string;
  row: NotificationDefaultsSetting;
  draft: DraftRow;
  timingMenuMinutes: readonly number[];
  disabled: boolean;
  onDraft: (next: DraftRow) => void;
}): ReactElement {
  const shown = shownRow(row, draft).offsetsMinutes;

  return (
    <fieldset className="mt-3 pl-7" disabled={disabled}>
      <legend className="text-body-sm font-semibold text-foreground">Send it</legend>
      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
        {[...timingMenuMinutes].reverse().map((minutes) => {
          const timeId = `${id}-time-${minutes}`;

          return (
            <div key={minutes} className="flex items-center gap-2">
              <Checkbox
                id={timeId}
                checked={shown.includes(minutes)}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  onDraft(withReminderTime(draft, shown, minutes, checked === true))
                }
              />
              <Label htmlFor={timeId} className="text-body-sm font-normal">
                {reminderTimeLabel(minutes)}
              </Label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

function SourceLine({
  layer,
  draft,
  onFollowPractice,
}: {
  layer: EditorLayer;
  draft: DraftRow;
  onFollowPractice: () => void;
}): ReactElement | null {
  if (layer === "practice") return null;

  if (followsPractice(draft)) {
    return <p className="mt-1 text-meta text-muted-foreground">Practice default</p>;
  }

  return (
    <p className="mt-1 flex items-center gap-2 text-meta text-muted-foreground">
      Changed from the practice default
      <Button
        type="button"
        variant="quiet-link"
        size="sm"
        className="h-auto p-0 text-meta"
        onClick={onFollowPractice}
      >
        Use the practice default
      </Button>
    </p>
  );
}

function MessageTypeRow({
  idPrefix,
  layer,
  row,
  draft,
  timingMenuMinutes,
  disabled,
  onDraft,
}: {
  idPrefix: string;
  layer: EditorLayer;
  row: NotificationDefaultsSetting;
  draft: DraftRow;
  timingMenuMinutes: readonly number[];
  disabled: boolean;
  onDraft: (next: DraftRow) => void;
}): ReactElement {
  const id = `${idPrefix}-${row.kind}`;
  const shown = shownRow(row, draft);

  return (
    <li className="py-4 first:pt-0 last:pb-0" data-message-type={row.kind}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          className="mt-0.5"
          checked={shown.enabled}
          disabled={disabled}
          onCheckedChange={(checked) => onDraft(withEnabled(draft, checked === true))}
        />
        <div className="min-w-0">
          <Label htmlFor={id} className="text-body font-semibold text-foreground">
            {labelOf(row.kind)}
          </Label>
          <p className="text-body-sm text-muted-foreground">
            {describeDescription(row.kind)}
          </p>
          <SourceLine
            layer={layer}
            draft={draft}
            onFollowPractice={() => onDraft(FOLLOW_PRACTICE)}
          />
        </div>
      </div>
      {row.timed ? (
        <ReminderTimes
          id={id}
          row={row}
          draft={draft}
          timingMenuMinutes={timingMenuMinutes}
          disabled={disabled || (layer === "rd" && !shown.enabled)}
          onDraft={onDraft}
        />
      ) : null}
    </li>
  );
}

function StatusLine({ status }: { status: SaveStatus }): ReactElement | null {
  if (status.kind === "saving") {
    return (
      <p role="status" className="text-body-sm text-muted-foreground">
        Saving…
      </p>
    );
  }

  if (status.kind === "saved" && status.changedByOthers.length === 0) {
    return (
      <p role="status" className="text-body-sm text-muted-foreground">
        Saved.
      </p>
    );
  }

  return null;
}

function SaveNotice({ status }: { status: SaveStatus }): ReactElement | null {
  if (status.kind === "conflict") {
    return (
      <Alert variant="warning" className="mt-4" data-save-notice="conflict">
        <AlertDescription>
          Someone else changed these settings since you opened them, so nothing was
          saved. The page now shows the latest settings. Check them and save again.
        </AlertDescription>
      </Alert>
    );
  }

  if (status.kind === "saved" && status.changedByOthers.length > 0) {
    return (
      <Alert variant="info" className="mt-4" data-save-notice="changed">
        <AlertDescription>
          Saved. Someone else also changed {status.changedByOthers.join(", ")} since you
          opened this page. The page now shows the latest settings.
        </AlertDescription>
      </Alert>
    );
  }

  if (status.kind === "failed") {
    return (
      <Alert variant="danger" className="mt-4" data-save-notice="failed">
        <AlertDescription>{status.message}</AlertDescription>
      </Alert>
    );
  }

  return null;
}

export interface DefaultsEditorProps {
  idPrefix: string;
  layer: EditorLayer;
  view: NotificationDefaultsResponse;
  draft: DefaultsDraft;
  status: SaveStatus;
  problem: string | null;
  dirty: boolean;
  onDraft: (key: string, next: DraftRow) => void;
  onSave: () => void;
}

function EditorRows({
  idPrefix,
  layer,
  view,
  draft,
  status,
  onDraft,
}: DefaultsEditorProps): ReactElement {
  return (
    <ul className="divide-y divide-border">
      {editorRows(view).map((row) => {
        const key = rowKey(row);
        const drafted = draft[key];
        if (!drafted) return null;

        return (
          <MessageTypeRow
            key={key}
            idPrefix={idPrefix}
            layer={layer}
            row={row}
            draft={drafted}
            timingMenuMinutes={view.timingMenuMinutes}
            disabled={status.kind === "saving"}
            onDraft={(next) => onDraft(key, next)}
          />
        );
      })}
    </ul>
  );
}

function EditorFooter({ status, problem, dirty }: DefaultsEditorProps): ReactElement {
  const blocked = status.kind === "saving" || !dirty || problem !== null;

  return (
    <>
      <SaveNotice status={status} />
      {problem ? (
        <p role="alert" className="mt-4 text-body-sm text-danger">
          {problem}
        </p>
      ) : null}
      <div className="mt-5 flex items-center gap-3">
        <Button type="submit" variant="press" disabled={blocked}>
          Save
        </Button>
        <StatusLine status={status} />
      </div>
    </>
  );
}

/**
 * One defaults layer's editor: a checkbox per Notification Kind, the reminder's
 * times from Healthie's fixed menu, and Save. On an RD layer each row says
 * whether it follows the practice default and can go back to it.
 */
export function DefaultsEditor(props: DefaultsEditorProps): ReactElement {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave();
      }}
    >
      <EditorRows {...props} />
      <EditorFooter {...props} />
    </form>
  );
}

/** The Practice defaults section's RD picker; "" means none picked. */
export function RdPicker({
  rds,
  value,
  onChange,
}: {
  rds: readonly NotificationDefaultsRdOption[];
  value: string;
  onChange: (rdId: string) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="notification-defaults-rd" className="text-body-sm font-semibold">
        RD
      </Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id="notification-defaults-rd" className="w-full sm:w-80">
          <SelectValue placeholder="Choose an RD" />
        </SelectTrigger>
        <SelectContent>
          {rds.map((rd) => (
            <SelectItem key={rd.id} value={rd.id}>
              {rd.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export interface MyAlertsEditorProps {
  view: MyAlertsResponse;
  draft: MyAlertsDraft;
  /** The viewer's time zone, the one the agenda time is shown and saved in. */
  zone: string;
  status: SaveStatus;
  problem: string | null;
  dirty: boolean;
  onDraft: (next: MyAlertsDraft) => void;
  onSave: () => void;
}

function alertLabel(field: string, leadMinutes: number, fallback: string): string {
  return field === "reminderEnabled"
    ? `${leadMinutes} minutes before each visit`
    : fallback;
}

function AlertSwitches({
  view,
  draft,
  status,
  onDraft,
}: MyAlertsEditorProps): ReactElement {
  return (
    <ul className="flex flex-col gap-3">
      {MY_ALERT_SWITCHES.map(({ field, label }) => {
        const id = `my-alerts-${field}`;

        return (
          <li key={field} className="flex items-center gap-3">
            <Checkbox
              id={id}
              checked={draft[field]}
              disabled={status.kind === "saving"}
              onCheckedChange={(checked) =>
                onDraft({ ...draft, [field]: checked === true })
              }
            />
            <Label htmlFor={id} className="text-body text-foreground">
              {alertLabel(field, view.preferences.reminderLeadMinutes, label)}
            </Label>
          </li>
        );
      })}
    </ul>
  );
}

function AgendaControls({
  draft,
  zone,
  status,
  onDraft,
}: MyAlertsEditorProps): ReactElement {
  const saving = status.kind === "saving";

  return (
    <div className="mt-5 border-t border-border pt-5">
      <div className="flex items-center gap-3">
        <Checkbox
          id="my-alerts-agendaEnabled"
          checked={draft.agendaEnabled}
          disabled={saving}
          onCheckedChange={(checked) =>
            onDraft(withAgendaEnabled(draft, checked === true, zone))
          }
        />
        <Label htmlFor="my-alerts-agendaEnabled" className="text-body text-foreground">
          Send me my daily agenda
        </Label>
      </div>
      <div className="mt-3 flex items-center gap-3 pl-7">
        <Label htmlFor="my-alerts-agenda-time" className="text-body-sm">
          Send it at
        </Label>
        <Input
          id="my-alerts-agenda-time"
          type="time"
          className="w-36"
          value={minuteToTime(draft.agendaMinuteOfDay)}
          disabled={saving || !draft.agendaEnabled}
          onChange={(event) =>
            onDraft(withAgendaTime(draft, timeToMinute(event.target.value), zone))
          }
        />
        <span className="text-meta text-muted-foreground">
          {draft.agendaTimeZone ?? zone}
        </span>
      </div>
    </div>
  );
}

/**
 * The signed-in RD's own alerts (NO-832): the visit alert, booked, moved and
 * cancelled alerts, and the daily agenda with its time. Each goes by email
 * and in the rail's Notifications.
 */
export function MyAlertsEditor(props: MyAlertsEditorProps): ReactElement {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave();
      }}
    >
      {props.view.switchedOn ? null : (
        <p
          className="mb-4 text-body-sm text-muted-foreground"
          data-alerts-note="healthie"
        >
          Healthie still sends your alerts for now. These choices apply once NDD sends
          them.
        </p>
      )}
      <AlertSwitches {...props} />
      <AgendaControls {...props} />
      <SaveNotice status={props.status} />
      {props.problem ? (
        <p role="alert" className="mt-4 text-body-sm text-danger">
          {props.problem}
        </p>
      ) : null}
      <div className="mt-5 flex items-center gap-3">
        <Button
          type="submit"
          variant="press"
          disabled={
            props.status.kind === "saving" || !props.dirty || props.problem !== null
          }
        >
          Save
        </Button>
        <StatusLine status={props.status} />
      </div>
    </form>
  );
}
